import { ServerResponse } from "http"
import { WithRouterProps } from "next/dist/client/with-router"
import Error from "next/dist/pages/_error"
import Head from "next/head"
import { Router, withRouter } from "next/router"
import React from "react"
import styled, { css } from "styled-components"

import Pc from "~/components/atoms/Pc"
import Sp from "~/components/atoms/Sp"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Pagination from "~/components/molecules/Pagination"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import { Color, TextSize, IsPC, IsMobile } from "~/consts/style-mixins"
import { Event } from "~/entities/Event"
import { PageInfo } from "~/entities/PageInfo"
import RecruitmentList from "~/observers/RecruitmentList"
import TabBar from "~/observers/TabBar"
import { canonicalUrl, internalLink, staticPath } from "~/services/helper"
import RootStore from "~/stores/RootStore"

interface Props {
  events: Event[]
  pageInfo: PageInfo
  pathname: string
}

class Events extends React.Component<Props & WithRouterProps> {
  static async getInitialProps({
    initialStoreData,
    pathname,
    query,
    res,
  }: {
    initialStoreData: RootStore
    pathname: string
    query: any
    res: ServerResponse
  }) {
    const { eventStore } = initialStoreData
    const { events, pageInfo } = await eventStore.fetch({
      page: query.page || 1,
    })
    // NOTE: 存在しないページ数が指定された際は 404 を返す
    const { currentPage, lastPage } = pageInfo as PageInfo
    if (currentPage !== 1 && currentPage > lastPage) {
      res.statusCode = 404
    }
    return { events, pageInfo, pathname }
  }

  render() {
    const { events, pageInfo, pathname, router } = this.props
    const crumbs = [
      {
        name: "イベント・説明会一覧",
        link: internalLink(`/events`),
      },
    ]

    const { currentPage, lastPage } = pageInfo
    if (currentPage !== 1 && currentPage > lastPage) {
      return <Error statusCode={404} />
    }

    return (
      <>
        <Head>
          <title>イベント・説明会一覧 | Infra（インフラ）</title>
          <meta
            name="description"
            content="企業が開催するイベントや説明会情報一覧ページです。大学3年生はもちろん、1, 2年生の方でも参加できるイベントを掲載しています。早期に自分のやりたいことやキャリア観を研ぎ澄ますため、いまから参加してみよう"
          />
          <meta name="og:title" content="イベント・説明会一覧 | Infra（インフラ）" />
          <meta
            name="og:description"
            content="企業が開催するイベントや説明会情報一覧ページです。大学3年生はもちろん、1, 2年生の方でも参加できるイベントを掲載しています。早期に自分のやりたいことやキャリア観を研ぎ澄ますため、いまから参加してみよう"
          />
          <meta property="og:image" content={`${process.env.APP_HOST + staticPath("/static/images/ogp.png")}`} />
          <link key="canonical" rel="canonical" href={canonicalUrl(router.pathname, router.query)} />
        </Head>
        <Header showTab selectedTab="Event" logoType="Events" />
        <Container size="Large">
          <StyledInner>
            <Pc>
              <StyledTypography type="SubHero">
                <h1>イベント・説明会一覧</h1>
              </StyledTypography>
            </Pc>
            <Sp>
              <StyledTypography type="SubHero">
                <h1>イベント・説明会一覧</h1>
              </StyledTypography>
            </Sp>
            <StyledRecruitmentNum>全{pageInfo.total}件の募集</StyledRecruitmentNum>
            <StyledListItemsWrapper>
              <StyledRecruitmentList type="event" recruitments={events} />
            </StyledListItemsWrapper>
            <Pagination
              baseUrl={pathname}
              queryStrings=""
              currentPage={pageInfo.currentPage}
              maxPage={pageInfo.lastPage}
              maxCount={pageInfo.total}
              currentMin={pageInfo.from}
              currentMax={pageInfo.to}
            />
          </StyledInner>
        </Container>
        <Breadcrumb crumbs={crumbs} />
        <Sp>
          <TabBar selectedTabIndex={0} />
        </Sp>
        <Footer />
      </>
    )
  }
}

const StyledTypography = styled(Typography)`
  margin-bottom: 8px;
`

const StyledInner = styled.div`
  ${IsPC(css`
    padding: 48px 0;
    min-height: calc(100vh - 243px);
  `)}
  ${IsMobile(css`
    padding: 24px 0;
    min-height: calc(100vh - 358px);
  `)}
`

const StyledRecruitmentNum = styled.div`
  margin-bottom: 24px;
  font-size: ${TextSize.S}px;
  color: ${Color.LightGray};
`

const StyledListItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${IsPC(css`
    margin: 0 -8px;
  `)}
`

const StyledRecruitmentList = styled(RecruitmentList)`
  margin-bottom: 24px;
  ${IsPC(css`
    width: 33.3%;
    padding: 0 8px;
  `)}
`

export default withRouter(Events)
